<template>
  <Card variant="outline" size="small" class="platform-card" :class="{ selected }">
    <View align="center" justify="space-between">
      <View direction="row" gap="md" align="center" class="platform-info">
        <View class="platform-icon">
          <img :src="logo" :alt="name" class="platform-logo" />
        </View>
        <View direction="column" gap="xs">
          <Text variant="label-medium" color="primary" class="platform-name">{{ name }}</Text>
          <Text v-if="description" variant="body-small" color="secondary">{{ description }}</Text>
        </View>
      </View>
      <View direction="row" gap="md" align="center" class="platform-controls">
        <span v-if="status" class="platform-status" :class="status.color">
          <span class="platform-status__dot" />
          {{ status.label }}
        </span>
        <Switch v-model="selected" />
      </View>
    </View>
  </Card>
</template>

<script setup lang="ts">
import { Card, Switch, Text, View } from '@tallo/design-system';

interface SyndicationPlatformStatus {
  label: string;
  color: 'green' | 'red' | 'grey';
}

interface SyndicationPlatformCardProps {
  logo: string;
  name: string;
  description?: string;
  status?: SyndicationPlatformStatus | null;
}

defineProps<SyndicationPlatformCardProps>();

const selected = defineModel<boolean>();
</script>

<style scoped lang="scss">
.platform-card {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid var(--t-color-neutral-300);

  &:hover {
    border-color: var(--t-color-primary-400);
    background-color: var(--t-color-primary-50);
  }

  &.selected {
    border-color: var(--t-color-primary-500);
    background-color: var(--t-color-primary-100);
  }
}

.platform-info {
  flex: 1;
}

.platform-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--t-border-radius-sm);
  background-color: var(--t-color-neutral-100);
  flex-shrink: 0;
}

.platform-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: var(--t-border-radius-xs);
}

.platform-name {
  font-weight: 600;
}

.platform-controls {
  flex-shrink: 0;
}

.platform-status {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font: var(--t-typography-body-sm-font);
  white-space: nowrap;

  &__dot {
    width: 0.375rem;
    height: 0.375rem;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &.green {
    color: var(--t-color-green-700);
  }
  &.green .platform-status__dot {
    background-color: var(--t-color-green-500);
  }

  &.red {
    color: var(--t-color-red-700);
  }
  &.red .platform-status__dot {
    background-color: var(--t-color-red-400);
  }

  &.grey {
    color: var(--t-color-neutral-600);
  }
  &.grey .platform-status__dot {
    background-color: var(--t-color-neutral-400);
  }
}
</style>
